import { useAuthContext } from "../../auth/state/AuthContext";
import type { AdminAiScanKind, AdminAiScanLoadState } from "../models/admin-ai-scans.models";
import { getAdminAiScanReports } from "../services/admin-ai-scans.service";
import { getAdminMonitoringErrorMessage } from "../services/admin-monitoring.service";
import { useEffect, useState } from "react";

// Each mounted scan owns its requests and refresh cycle.
// Review pages also load the feature inventory so missing scans remain visibly unreviewed.
export const useAdminAiScanReports = (scan: AdminAiScanKind) => {
  const accountId = useAuthContext().state.user?.accessToken?.sub ?? "";
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<AdminAiScanLoadState>({
    key: "",
    response: null,
    inventory: null,
    error: null,
    loading: true,
  });
  const key = `${accountId}:${scan}:${revision}`;

  useEffect(() => {
    const controller = new AbortController();
    setResult({ key, response: null, inventory: null, error: null, loading: true });

    // Settle independently: a missing inventory request must not hide valid scan reports.
    Promise.allSettled([
      getAdminAiScanReports(scan, controller.signal),
      scan === "features" ? Promise.resolve(null) : getAdminAiScanReports("features", controller.signal),
    ]).then(([scanResult, featureResult]) => {
      if (controller.signal.aborted) {
        return;
      }

      const errors = [scanResult, featureResult]
        .filter((entry): entry is PromiseRejectedResult => entry.status === "rejected")
        .map((entry) => getAdminMonitoringErrorMessage(entry.reason));
      setResult({
        key,
        response: scanResult.status === "fulfilled" ? scanResult.value : null,
        inventory: featureResult.status === "fulfilled" ? featureResult.value : null,
        error: errors.length ? errors.join(" ") : null,
        loading: false,
      });
    });

    return () => controller.abort();
  }, [key, scan]);

  const current = result.key === key ? result : { response: null, inventory: null, error: null, loading: true };

  return {
    reports: current.response?.reports ?? [],
    featureReports: scan === "features" ? current.response?.reports ?? [] : current.inventory?.reports ?? [],
    issues: [
      ...(current.response?.issues ?? []),
      ...(current.inventory?.issues.map((issue) => ({ ...issue, path: `features/${issue.path}` })) ?? []),
    ],
    available: current.response?.available ?? false,
    retrievedAt: current.response?.retrievedAt ?? null,
    error: current.error,
    loading: current.loading,
    refresh: () => setRevision((value) => value + 1),
  };
};
